import { FC, useEffect, useMemo, useState, MouseEvent } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useNotificationControllerGetUnseenNotificationCountQuery,
  useNotificationControllerGetPreviewUnseenNotificationQuery,
} from 'src/app/services/api.generated';
import { NotificationListRow } from 'src/components/organisms/notification/NotificationListRow';
import { useNavigate } from 'react-router';
import LaunchIcon from '@mui/icons-material/Launch';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';
import { NotificationListRowSkeleton } from './NotificationListRowSkeleton';

const intervalDuration = 60000;
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

export const NotificationCenter: FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDemoUser = useIsDemoUser();

  const goToNotificationsPage = () => {
    setAnchorEl(null);
    navigate('/notifications');
  };

  const {
    data: unseenNotifications = [],
    isLoading: getUnseenNotifLoading,
    refetch: refetchUnseenNotif,
  } = useNotificationControllerGetPreviewUnseenNotificationQuery(undefined, {
    skip: !anchorEl || isDemoUser,
  });

  const { data: unseenNotifCount, refetch: unseenNotifCountRefetch } =
    useNotificationControllerGetUnseenNotificationCountQuery(undefined, {
      skip: isDemoUser,
    });

  useEffect(() => {
    if (isDemoUser) return;
    if (anchorEl) return;
    const getunseenNotifCountInterval = setInterval(() => {
      unseenNotifCountRefetch();
    }, intervalDuration);
    return () => clearInterval(getunseenNotifCountInterval);
  }, [anchorEl, isDemoUser, unseenNotifCountRefetch]);

  useEffect(() => {
    if (isDemoUser) return;
    if (!anchorEl) return;
    const getUnseenNotifListInterval = setInterval(() => {
      refetchUnseenNotif();
    }, intervalDuration);
    return () => clearInterval(getUnseenNotifListInterval);
  }, [anchorEl, isDemoUser, refetchUnseenNotif]);

  const hasNewNotif = useMemo(
    () => (unseenNotifCount ? unseenNotifCount.notificationCount > 0 : false),
    [unseenNotifCount]
  );

  const openPopoverHandler = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const closePopoverHandler = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? 'notificationPopover' : undefined;

  return (
    <>
      <Tooltip title={t('notifications') as string}>
        <IconButton
          size="small"
          onClick={openPopoverHandler}
          aria-label={t('notifications') as string}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ width: 34, height: 34, color: 'text.secondary' }}
        >
          <Badge
            color="primary"
            badgeContent={unseenNotifCount?.notificationCount}
            invisible={!hasNewNotif}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                height: 16,
                minWidth: 16,
                px: 0.5,
              },
            }}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: '1.35rem' }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={closePopoverHandler}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        TransitionProps={{
          timeout: 180,
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'customGrey.light',
            width: 'clamp(280px, 100%, 360px)',
            overflow: 'hidden',
            borderRadius: '10px',
            borderTopRightRadius: 4,
            transformOrigin: 'top right',
            '@keyframes notifPopIn': {
              from: { opacity: 0, transform: 'scale(0.96)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
            animation: `notifPopIn 180ms ${EASE_OUT}`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'customGrey.grey3',
          }}
        >
          <Typography fontWeight={600} fontSize={15}>
            {t('notificationCenter')}
          </Typography>
          <Tooltip title={t('showAllNotif')}>
            <IconButton size="small" onClick={goToNotificationsPage}>
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {!hasNewNotif ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%', height: 160, px: 2 }}
            >
              <Typography align="center" fontSize={13} sx={{ opacity: 0.5 }}>
                {t('noNotification')}
              </Typography>
            </Stack>
          ) : getUnseenNotifLoading ? (
            [...Array(3)].map((_, index) => (
              <NotificationListRowSkeleton key={index} />
            ))
          ) : (
            unseenNotifications.map((item) => (
              <Box
                key={item.id}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'customGrey.grey3',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <NotificationListRow
                  notification={item}
                  unseen
                  compact
                  showDismiss
                  onNavigated={closePopoverHandler}
                />
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};
